import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useTheme } from '@/contexts/ThemeContext'
import PendingTransactionCard from './PendingTransactionCard'
import type { PendingTransaction } from './types'
import { EthSafeTransaction } from '@safe-global/protocol-kit'
import SectionHeader from '@/components/shared/SectionHeader'

interface PendingTransactionsProps {
  pendingTransactions: PendingTransaction[]
  creatingTransactions: Map<number, string>
  isLoading: boolean
  userAddress: string | undefined
  treasurySafe: string | undefined
  stngProxy: string | undefined
  operationalSafeFactory: string | undefined
  isMobileOrTablet: boolean
  safeThreshold: number | null
  signingTxHash: string | null
  executingTxHash: string | null
  rejectingTxHash: string | null
  simulatingTxHash: string | null
  simulationResults: Record<string, { success: boolean; error?: string }>
  isConfirmingExecute: boolean
  showExecuteOverlay: boolean
  executingTxNonceForOverlay: number | null
  isSafeReady: boolean
  userOperationalSafes: Set<string>
  onRefresh: () => void
  onSign: (tx: EthSafeTransaction, hash: string, safeAddress: string) => void
  onExecute: (
    tx: EthSafeTransaction,
    hash: string,
    safeAddress: string,
    isReady?: boolean
  ) => void
  onSimulate: (
    tx: EthSafeTransaction,
    hash: string,
    safeAddress: string
  ) => void
  onReject: (tx: EthSafeTransaction, hash: string, safeAddress: string) => void
  onCopyHash: (hash: string) => void
}

export default function PendingTransactions({
  pendingTransactions,
  creatingTransactions,
  isLoading,
  userAddress,
  treasurySafe,
  stngProxy,
  operationalSafeFactory,
  isMobileOrTablet,
  safeThreshold,
  signingTxHash,
  executingTxHash,
  rejectingTxHash,
  simulatingTxHash,
  simulationResults,
  isConfirmingExecute,
  showExecuteOverlay,
  executingTxNonceForOverlay,
  isSafeReady,
  userOperationalSafes,
  onRefresh,
  onSign,
  onExecute,
  onSimulate,
  onReject,
  onCopyHash,
}: PendingTransactionsProps) {
  const { colors } = useTheme()

  return (
    <Box sx={{ mt: 3 }}>
      <SectionHeader
        title="Pending Transactions"
        action={
          <Tooltip title="Refresh Pending Transactions">
            <span>
              <IconButton
                onClick={onRefresh}
                disabled={isLoading}
                sx={{
                  color: colors.primary,
                  '&:hover': {
                    backgroundColor: `${colors.primary}22`,
                  },
                  '&:disabled': {
                    color: colors.text,
                    opacity: 0.5,
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: isLoading ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': {
                        transform: 'rotate(0deg)',
                      },
                      '100%': {
                        transform: 'rotate(360deg)',
                      },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        }
      />
      <Box
        sx={{
          backgroundColor: colors.overlay,
          borderRadius: 2,
          p: 2,
          border: `1px solid ${colors.primary}`,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            }}
          >
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {Array.from(creatingTransactions.entries()).map(([nonce, type]) => (
              <Card
                key={`creating-${nonce}`}
                sx={{
                  mb: 2,
                  backgroundColor: colors.background,
                  border: `1px solid ${colors.primary}`,
                  position: 'relative',
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      {type && (
                        <Chip
                          label={type}
                          size="small"
                          sx={{
                            backgroundColor: colors.primary,
                            color: colors.background,
                            fontWeight: 600,
                          }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text,
                          opacity: 0.7,
                        }}
                      >
                        Creating transaction...
                      </Typography>
                    </Box>
                    <CircularProgress
                      size={20}
                      sx={{ color: colors.primary }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
            {pendingTransactions.length === 0 &&
            creatingTransactions.size === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  py: 4,
                }}
              >
                <Typography
                  sx={{
                    color: colors.text,
                    opacity: 0.7,
                    fontStyle: 'italic',
                  }}
                >
                  No pending transactions
                </Typography>
              </Box>
            ) : (
              pendingTransactions.map((transaction) => {
                const { tx, hash, isReady, safeAddress } = transaction
                if (!safeAddress) return null

                const signaturesCount = tx.signatures?.size || 0
                const hasUserSigned = Boolean(
                  userAddress && tx.signatures?.has(userAddress.toLowerCase())
                )
                const isSigning = signingTxHash === hash
                const isExecuting = executingTxHash === hash
                const isRejecting = rejectingTxHash === hash
                const isConfirmingThisTx =
                  isConfirmingExecute && executingTxHash === hash
                const canExecute = isReady === true
                const isTreasurySafe =
                  safeAddress.toLowerCase() === treasurySafe?.toLowerCase()
                const canReject =
                  !isRejecting &&
                  !isExecuting &&
                  !isSigning &&
                  (isTreasurySafe
                    ? isSafeReady
                    : userOperationalSafes.has(safeAddress.toLowerCase()))
                const showOverlayForThisTx =
                  showExecuteOverlay &&
                  executingTxNonceForOverlay !== null &&
                  tx.data.nonce === executingTxNonceForOverlay

                return (
                  <PendingTransactionCard
                    key={hash}
                    transaction={transaction}
                    userAddress={userAddress}
                    treasurySafe={treasurySafe}
                    stngProxy={stngProxy}
                    operationalSafeFactory={operationalSafeFactory}
                    isMobileOrTablet={isMobileOrTablet}
                    safeThreshold={safeThreshold}
                    signaturesCount={signaturesCount}
                    hasUserSigned={hasUserSigned}
                    isSigning={isSigning}
                    isExecuting={isExecuting}
                    isRejecting={isRejecting}
                    isConfirmingThisTx={isConfirmingThisTx}
                    canExecute={canExecute}
                    canReject={canReject}
                    showOverlay={showOverlayForThisTx}
                    simulationResult={simulationResults[hash]}
                    isSimulating={simulatingTxHash === hash}
                    onSign={() => onSign(tx, hash, safeAddress)}
                    onExecute={() => onExecute(tx, hash, safeAddress, isReady)}
                    onSimulate={() => onSimulate(tx, hash, safeAddress)}
                    onReject={() => onReject(tx, hash, safeAddress)}
                    onCopyHash={onCopyHash}
                    isSafeReady={isSafeReady}
                    userOperationalSafes={userOperationalSafes}
                  />
                )
              })
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}
